import { Radio } from 'lucide-react';
import type { Team, Vitals } from '@/lib/teams';
import { thermalColor } from '@/lib/temp';
import { Sparkline } from './Sparkline';
import { cn } from '@/lib/utils';

const STATUS_LABEL = { healthy: 'Healthy', warning: 'Warning', critical: 'Critical' } as const;

export function HubTile({
  team,
  vitals,
  onOpen,
}: {
  team: Team;
  vitals: Vitals;
  onOpen: () => void;
}) {
  const ident = `hsl(${team.hue} 65% 52%)`;
  const warmth = thermalColor(vitals.heat);
  const Icon = team.icon;

  return (
    <button
      onClick={onOpen}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-3xl p-5 text-left transition-all duration-300',
        'bg-white/40 dark:bg-black/20 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-xl hover:shadow-2xl',
        'hover:-translate-y-1',
      )}
    >
      {/* Background glow */}
      <div className="absolute top-0 right-0 p-32 opacity-10 blur-3xl rounded-full pointer-events-none transition-opacity group-hover:opacity-20" style={{ background: ident }} />

      {/* warmth edge (status as temperature) */}
      <span className="absolute inset-y-4 left-0 w-[4px] rounded-full" style={{ background: warmth }} />

      <div className="flex items-start justify-between relative z-10">
        <div className="flex items-center gap-3">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-sm"
            style={{ background: `hsl(${team.hue} 65% 52% / 0.15)`, color: ident }}
          >
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight leading-tight truncate" style={{ color: ident }}>{team.name}</div>
            <div className="mt-1 flex items-center gap-1.5 font-mono text-[11px] font-bold tracking-wide" style={{ color: warmth }}>
              <span className="h-2 w-2 rounded-full shadow-sm" style={{ background: warmth }} />
              {STATUS_LABEL[vitals.status]}
            </div>
          </div>
        </div>
        {team.oncall && (
          <span
            className="inline-flex items-center gap-1 rounded-full bg-white/50 dark:bg-black/40 px-2 py-1 font-mono text-[10px] text-theme opacity-80 shadow-sm backdrop-blur-sm"
            title={`On-call: ${team.oncall}`}
          >
            <Radio className="h-3 w-3" /> {team.oncall}
          </span>
        )}
      </div>

      {/* Primary vitals */}
      <div className="mt-5 grid grid-cols-4 gap-2 relative z-10">
        <Vital label="Total" value={vitals.total} />
        <Vital label="Open" value={vitals.open} />
        <Vital label="Unasg" value={vitals.unassigned} warn={vitals.unassigned > 0} />
        <Vital label="Assigned" value={vitals.assigned} />
        
        <Vital label="Pending" value={vitals.pending} />
        <Vital label="In Prog" value={vitals.in_progress} />
        <Vital label="On Hold" value={vitals.hold} />
        <Vital label="Resolved" value={vitals.resolved} />
      </div>
      
      {/* SLA and SLA breaches */}
      <div className="mt-4 flex gap-4 bg-white/30 dark:bg-black/30 rounded-xl p-3 border border-white/20 dark:border-white/5 relative z-10">
        <Vital label="SLA Breach" value={vitals.breaching} warn={vitals.breaching > 0} />
        <div className="w-[1px] bg-white/20 dark:bg-white/5" />
        <Vital label="Avg Res Time" value={`${vitals.avgResH}h`} />
      </div>

      {/* sparkline + capacity */}
      <div className="mt-4 flex items-end gap-3 relative z-10">
        <div className="h-10 flex-1">
          <Sparkline data={vitals.spark} color={ident} className="h-full w-full" />
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase font-bold text-theme opacity-60 tracking-wider mb-1">Load</div>
          <div className="font-mono text-sm font-bold bg-white/50 dark:bg-black/40 px-2 py-0.5 rounded-lg border border-white/20 dark:border-white/10 shadow-sm text-theme">{Math.round(vitals.capacity * 100)}%</div>
        </div>
      </div>

      {/* members */}
      <div className="mt-5 flex items-center justify-between border-t border-black/5 dark:border-white/5 pt-4 relative z-10">
        <div className="flex -space-x-2">
          {team.members.slice(0, 5).map((m) => (
            <span
              key={m}
              title={m}
              className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white dark:border-[#1a1b1e] bg-gradient-to-br from-gray-100 to-gray-300 dark:from-gray-700 dark:to-gray-800 font-mono text-[10px] font-bold text-theme shadow-sm"
            >
              {m.split(' ').map((p) => p[0]).join('').slice(0, 2)}
            </span>
          ))}
          {team.members.length > 5 && (
            <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white dark:border-[#1a1b1e] bg-white/50 dark:bg-black/40 font-mono text-[10px] font-bold text-theme opacity-70 backdrop-blur-sm">
              +{team.members.length - 5}
            </span>
          )}
        </div>
        <span className="font-mono text-[11px] font-bold opacity-0 transition-all group-hover:opacity-100 group-hover:translate-x-1" style={{ color: ident }}>
          View Team →
        </span>
      </div>
    </button>
  );
}

function Vital({
  label,
  value,
  warn,
}: {
  label: string;
  value: string | number;
  warn?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <div className="text-[10px] uppercase font-bold text-theme opacity-60 truncate pr-1 tracking-wider" title={label}>{label}</div>
      <div className={cn('font-mono text-lg font-bold mt-0.5 leading-none', warn ? 'text-red-500' : 'text-theme')}>{value}</div>
    </div>
  );
}
